
import React, { useState } from "react"

import { useQuery } from "@tanstack/react-query";

import { Link } from "react-router-dom";
import { Button, Col, Grid, Form, Row, Input, Space, Select, InputNumber, Popconfirm, Tooltip, Typography, Modal, Alert, notification, Empty, Divider } from "antd";
import { FileTextOutlined, MinusOutlined, PlusOutlined, QuestionCircleTwoTone, UsergroupAddOutlined } from "@ant-design/icons";

import { FiltroDeListagem, AlunoService } from "../../../Services/Domain/Pessoa/AlunoService";
import { Conjunto } from "../../../Components/Conjunto";
import { Formatacoes } from "../../../Utils/Formatacao";
import { Ordenar } from "../../../Utils/Ordenacao";
import { ResponsiveAntdTable } from "../../../Components/ResponsiveAntdTable";
import { Restrito } from "../../../Components/Restrito";
import { TypeAluno } from "../../../Models/Domain/Pessoa/Aluno";

import { InputComReactIMask } from "../../../Components/InputComReactIMask";
import { EnumTipoDeDocumento } from "../../../Models/Domain/Pessoa/Enum";
import { useForm } from "antd/lib/form/Form";

import { Permissoes } from "../../../Constants/Permissoes";
import { PropriedadesDoPopConfirm } from "../../../Defaults";
import { DescricaoDoAluno } from "../../Viagem/Cadastros/Viagem/Componentes/DescricaoDoAluno";
import { FormularioDeResponsaveisDoAluno } from "../../Viagem/Cadastros/Viagem/Componentes/FormularioDeResponsaveis";
import { TypeAlunoEResponsavel } from "../../../Models/Domain/Pessoa/AlunoEResponsavel";
import { PropriedadesDeNotificacao } from "../../../Utils/Notificacoes";
import { useRequerPermissoes } from "../../../Hooks/useRequerPermissoes";

import AdicionarAjuda, { ITutorial } from "../../../Components/AdicionarAjuda";
import * as Tutoriais from "./tutorial"
import ViagemService from "../../../Services/Domain/Viagem/ViagemService";
import Utilitario from "@/Utils/Utilitario";
import { FichaDeSaudeModal } from "@/Views/FichaDeSaude/Ficha";
import PessoaGeneroSelect from "@/Components/Populados/Pessoa/PessoaGenero/PessoaGeneroSelect";
import PessoaBloqueioBloquearModal from "@/Components/Populados/Pessoa/PessoaBloqueio/PessoaBloqueioBloquearModal";
import { SelectComPesquisa } from "@/Components/SelectComPesquisa";

export const ListagemDeAlunos: React.FC<{
    aoAdicionar?: () => void,
    aoEditar?: (aluno: TypeAluno) => any,

    hrefDoBotaoAdicionar?: string,
    hrefDoBotaoEditar?: (aluno: TypeAluno) => string | void
}> = (props) => {

    const requerPermissoes = useRequerPermissoes();

    const tamanhoDaTela = Grid.useBreakpoint();

    interface IFiltro extends FiltroDeListagem { };

    const [ form ] = useForm<IFiltro>()

    const [filtro, setFiltro] = useState<IFiltro>({
        ativo: true
    })

    const [statusDoModalDeVinculoDeResponsaveis, setStatusDoModalDeVinculoDeResponsaveis] = useState<{ exibir: boolean, aluno?: TypeAluno }>({ exibir: false });
    const [vinculoDeResponsaveis, setVinculoDeResponsaveis] = useState<TypeAlunoEResponsavel[]>([])

    const alunosDisponiveis = useQuery({
        queryKey: ["listarAlunos", filtro],
        queryFn: () => new AlunoService().listar({
            ...filtro,
            nome: filtro.nome?.trim() == ""? undefined : filtro.nome,
            cpf: filtro.cpf?.trim() == ""? undefined : Formatacoes.sanitizar(".-_", filtro.cpf),
            documento: filtro.documento?.trim() == ""? undefined : filtro.documento
        }),
        enabled: true,
    });

    const viagensDisponiveis = useQuery({
        queryKey: ["viagensDisponiveis"],
        queryFn: () => new ViagemService().listarViagens({ativo: true})
    })

    const onFinish = (valores: IFiltro) => {
        setFiltro({...valores, tipoDeDocumento: valores.documento? EnumTipoDeDocumento.RG : undefined})
    };

    const adicionarAberto = requerPermissoes.todas(Permissoes.CriarAluno);
    const [chavesDasLinhasExpandidas, setChavesDasLinhasExpandidas] = useState<React.Key[]>([])

    return (
        <React.Fragment>
            <Modal
                destroyOnClose
                width={"80vw"}
                title="Vínculo de responsáveis"
                maskClosable={false}
                open={statusDoModalDeVinculoDeResponsaveis.exibir == true}
                okButtonProps={{
                    disabled: vinculoDeResponsaveis.find((v) => v.principal) == undefined,
                }}
                onCancel={() => {
                    setVinculoDeResponsaveis([]);
                    setStatusDoModalDeVinculoDeResponsaveis({ exibir: false });
                }}
                onOk={() => {
                    const aluno = statusDoModalDeVinculoDeResponsaveis.aluno;
                    if (aluno?.codigo == undefined) return;

                    new AlunoService()
                        .patch(aluno.codigo, [
                            {
                                op: "replace",
                                path: "/responsaveis",
                                value: vinculoDeResponsaveis,
                            },
                        ])
                        .then(() => {
                            setVinculoDeResponsaveis([]);
                            setStatusDoModalDeVinculoDeResponsaveis({ exibir: false });
                            alunosDisponiveis.refetch();

                            notification.open({
                                ...PropriedadesDeNotificacao.Sucesso("Responsáveis atualizados com sucesso."),
                            });
                        })
                        .catch(() => {});
                }}
            >
                <div style={{ padding: 8 }}>
                    <FormularioDeResponsaveisDoAluno responsaveisAPI={[vinculoDeResponsaveis, setVinculoDeResponsaveis]} />

                    {vinculoDeResponsaveis.find((v) => v.principal) == undefined && (
                        <Alert showIcon type="error" message="É necessário definir ao menos um responsável principal" />
                    )}
                </div>
            </Modal>

            <Form
                form={form}
                onFinish={onFinish}
                autoComplete="off"
                layout="vertical"
                style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                id={Tutoriais.IDs.formularioDeFiltro}
            >
                <Row gutter={[8, 8]}>
                    <Col xs={24} xl={10} id={Tutoriais.IDs.filtroDeNome}>
                        <Form.Item<IFiltro> name="nome" label="Nome" style={{ marginBottom: 0 }}>
                            <Input placeholder="Nome" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={6} id={Tutoriais.IDs.filtroDeGenero}>
                        <Form.Item<IFiltro> name="genero" label="Gênero" style={{ marginBottom: 0 }}>
                            <PessoaGeneroSelect />
                            {/* <Select
                                placeholder="Selecione"
                                allowClear
                                options={[
                                    { value: EnumGenero.HOMEM, label: "Homem" },
                                    { value: EnumGenero.MULHER, label: "Mulher" },
                                    { value: EnumGenero.HOMEM_TRANS, label: "Homem trans" },
                                    { value: EnumGenero.MULHER_TRANS, label: "Mulher trans" },
                                    { value: EnumGenero.NÃO_BINÁRIO, label: "Não binário" },
                                ]}
                            /> */}
                        </Form.Item>
                    </Col>

                    <Col xs={12} xl={4} id={Tutoriais.IDs.filtroDeIdadeMaiorOuIgual}>
                        <Form.Item<IFiltro> name="idadeMaiorOuIgual" label="De ou igual" style={{ marginBottom: 0 }}>
                            <InputNumber min={0} max={50} style={{ width: "100%" }} placeholder="Anos" />
                        </Form.Item>
                    </Col>

                    <Col xs={12} xl={4} id={Tutoriais.IDs.filtroDeIdadeMenorQue}>
                        <Form.Item<IFiltro>
                            name="idadeMenorQue"
                            label={
                                <>
                                    <Typography.Text>
                                        Até{" "}
                                        <Tooltip title={<>A idade limite não é inclusiva. </>}>
                                            <QuestionCircleTwoTone />
                                        </Tooltip>
                                    </Typography.Text>{" "}
                                </>
                            }
                            style={{ marginBottom: 0 }}
                            dependencies={["idadeMaiorOuIgual"]}
                            rules={[
                                {
                                    validator(_, value) {
                                        const min = form.getFieldValue("idadeMaiorOuIgual");
                                        if (value != undefined && value <= min)
                                            return Promise.reject("A idade limite não pode ser inferior ou igual à idade base");
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                        >
                            <InputNumber min={0} max={50} style={{ width: "100%" }} placeholder="Anos" />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[8, 8]}>
                    <Col xs={24} xl={6} id={Tutoriais.IDs.filtroDeCPF}>
                        <Form.Item<IFiltro> name="cpf" label="CPF" style={{ marginBottom: 0 }}>
                            <InputComReactIMask maskOptions={{ mask: "000.000.000-00" }} />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={5} id={Tutoriais.IDs.filtroDeCPF}>
                        <Form.Item<IFiltro> name="rne" label="RNE" style={{ marginBottom: 0 }}>
                            <Input placeholder="RNE" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={5} id={Tutoriais.IDs.filtroDePassaporte}>
                        <Form.Item<IFiltro> name="passaporte" label="Passaporte" style={{ marginBottom: 0 }}>
                            <Input placeholder="Passaporte" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={5} id={Tutoriais.IDs.filtroDeDocumento}>
                        <Form.Item<IFiltro>
                            name="documento"
                            label={<>RG</>}
                            style={{ flex: 2, marginBottom: 0 }}
                            rules={[
                                {
                                    transform(value) {
                                        if (value.trim() == "") return undefined;
                                        return value;
                                    },
                                },
                            ]}
                        >
                            <Input id={Tutoriais.IDs.filtroDeValorDoDocumento} placeholder="Valor" />
                        </Form.Item>
                    </Col>

                    <Col xs={24} xl={3} id={Tutoriais.IDs.filtroDeAtivo}>
                        <Form.Item<IFiltro> name="ativo" label="Ativo" initialValue={true} style={{ marginBottom: 0 }}>
                            <Select
                                allowClear
                                placeholder={"Todos"}
                                options={[
                                    { value: true, label: "Ativo" },
                                    { value: false, label: "Inativo" },
                                ]}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[8, 8]}>
                    <Col xs={24} xl={24} id={Tutoriais.IDs.filtroDeInclusoNasViagens}>
                        <Form.Item<IFiltro> name="inclusoNasViagens" label="Incluso nas viagens" style={{ marginBottom: 0 }}>
                            <SelectComPesquisa
                                mode="multiple"
                                loading={viagensDisponiveis.isFetching}
                                allowClear
                                options={viagensDisponiveis.data?.data.map((v) => ({
                                    value: v.codigo,
                                    label: v.titulo,
                                }))}
                                placeholder="Selecione"
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={[8, 8]}>
                    <Col xs={12} xl={3} id={Tutoriais.IDs.botaoListar}>
                        <Form.Item>
                            <Button type="primary" htmlType="submit" disabled={alunosDisponiveis.isFetching} style={{ width: "100%" }}>
                                Listar
                            </Button>
                        </Form.Item>
                    </Col>

                    <Restrito aberto={adicionarAberto}>
                        <Col xs={12} xl={3} id={Tutoriais.IDs.botaoAdicionar}>
                            <Form.Item style={{ width: "100%" }}>
                                <Link
                                    to={props.hrefDoBotaoAdicionar ?? "#"}
                                    onClick={(e) => {
                                        !props.hrefDoBotaoAdicionar && e.preventDefault();
                                    }}
                                >
                                    <Button
                                        type="primary"
                                        onClick={() => {
                                            props.aoAdicionar?.();
                                        }}
                                        style={{ width: "100%" }}
                                    >
                                        Adicionar
                                    </Button>
                                </Link>
                            </Form.Item>
                        </Col>
                    </Restrito>
                </Row>
            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeAluno>
                        bordered
                        locale={{ emptyText: "Nenhum aluno encontrado." }}
                        dataSource={alunosDisponiveis.data?.data}
                        loading={alunosDisponiveis.isFetching}
                        rowKey={(k) => k.codigo!}
                        addTitleOn="lg"
                        expandable={{
                            showExpandColumn: false,
                            expandedRowRender: (record) => (
                                <div>
                                    <DescricaoDoAluno ocultarTitulo aluno={record} />
                                </div>
                            ),
                            rowExpandable: () => true,
                            expandedRowKeys: chavesDasLinhasExpandidas,
                        }}
                        columns={[
                            {
                                title: "Código",
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                dataIndex: "codigo",
                                sorter: (a, b) => Ordenar.Numero(a.codigo, b.codigo),
                                key: "aluno-codigo",
                            },
                            {
                                title: "Ativo",
                                dataIndex: ["ativo"],
                                breakpoints: { xs: 12, sm: 12, lg: 2 },
                                key: "aluno-ativo",
                                sorter: (a, b) => Ordenar.Booleano(a.ativo, b.ativo),
                                render: (v: boolean) => {
                                    return <>{v ? "Sim" : "Não"}</>;
                                },
                            },
                            {
                                title: "Nome",
                                breakpoints: { xs: 24, sm: 12, lg: 7 },
                                dataIndex: ["pessoa", "nomeExibicao"],
                                sorter: (a, b) => Ordenar.String(a.pessoa?.nome, b.pessoa?.nome),
                                key: "aluno-nome",
                                render: (_, r) => {
                                    const nome = r?.pessoa?.nome;
                                    const nomeSocial = r?.pessoa?.nomeSocial;

                                    return (
                                        <>
                                            {nomeSocial && nomeSocial != nome ? (
                                                <>
                                                    {nomeSocial}
                                                    <br/>
                                                    ({nome})
                                                </>
                                            ) : (
                                                <>{nome}</>
                                            )}
                                        </>
                                    );
                                },
                            },
                            {
                                title: "Documentos",
                                breakpoints: { xs: 24, sm: 12, lg: 8 },
                                noResponsiveTitle: true,
                                render: (_, record) => {
                                    const x = [
                                        [Formatacoes.formatarCPF(record.pessoa?.cpf), "CPF"],
                                        [record.pessoa?.rne, "RNE"],
                                        [record.pessoa?.passaporte, "Passaporte"],
                                        [record.documento, Utilitario.enumParaString(EnumTipoDeDocumento, record.tipoDeDocumento)],
                                    ].filter((k) => k[0] != undefined);

                                    return (
                                        <Typography>
                                            {x.length == 0
                                                ? "N/A"
                                                : x.map((k, i) => (
                                                      <React.Fragment key={k[1]}>
                                                          {i > 0 && <br />}
                                                          <Typography.Text strong>{k[1]}: </Typography.Text>
                                                          <Typography.Text>{k[0]}</Typography.Text>
                                                      </React.Fragment>
                                                  ))}
                                        </Typography>
                                    );
                                },
                                key: "aluno-documento-principal",
                            },
                            {
                                title: "Ações",
                                key: "acoes",
                                noResponsiveTitle: true,
                                breakpoints: { xs: 24, sm: 24, lg: 5 },
                                render: (_, item) => {
                                    return (
                                        <Conjunto
                                            style={{ justifyContent: tamanhoDaTela.sm ? "unset" : "center", display: "flex", flexWrap: "wrap" }}
                                            itens={[
                                                <Button
                                                    type="dashed"
                                                    size="small"
                                                    icon={item.codigo && chavesDasLinhasExpandidas.includes(item.codigo) ? <MinusOutlined /> : <PlusOutlined />}
                                                    onClick={() => {
                                                        if (!item.codigo) return;

                                                        if (chavesDasLinhasExpandidas.includes(item.codigo)) {
                                                            setChavesDasLinhasExpandidas((prev) => prev.filter((v) => v != item.codigo));
                                                        } else {
                                                            setChavesDasLinhasExpandidas((prev) => [...prev, item.codigo!]);
                                                        }
                                                    }}
                                                ></Button>,
                                                ...(() => {
                                                    if (item.ativo) {
                                                        return [
                                                            requerPermissoes.todas(Permissoes.AtualizarAluno) && (
                                                                <Button
                                                                    type="dashed"
                                                                    size="small"
                                                                    icon={
                                                                        <Tooltip title="Responsáveis">
                                                                            <UsergroupAddOutlined />
                                                                        </Tooltip>
                                                                    }
                                                                    onClick={() => {
                                                                        setVinculoDeResponsaveis(item.responsaveis ?? []);
                                                                        setStatusDoModalDeVinculoDeResponsaveis({ exibir: true, aluno: item });
                                                                    }}
                                                                ></Button>
                                                            ),

                                                            item.codigo && <FichaDeSaudeModal
                                                                buttonOpen={
                                                                    <Button
                                                                        type="dashed"
                                                                        size="small"
                                                                        icon={
                                                                            <Tooltip title="Ficha de saúde">
                                                                                <FileTextOutlined />
                                                                            </Tooltip>
                                                                        }
                                                                    />
                                                                }
                                                                fichaProps={{privilegio: "administrador", codigoDoAluno: item.codigo, permitirCompartilhamento: true}}
                                                            />,

                                                            requerPermissoes.todas(Permissoes.AtualizarAluno) && (
                                                                <Link
                                                                    to={props.hrefDoBotaoEditar?.(item) ?? "#"}
                                                                    onClick={(e) => !props.hrefDoBotaoEditar && e.preventDefault()}
                                                                >
                                                                    <Button
                                                                        className={Tutoriais.Classes.botaoEditar}
                                                                        type="primary"
                                                                        size="small"
                                                                        onClick={() => {
                                                                            props.aoEditar?.(item);
                                                                        }}
                                                                    >
                                                                        Editar
                                                                    </Button>
                                                                </Link>
                                                            ),

                                                            requerPermissoes.todas(Permissoes.DesativarAluno) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("desativar")}
                                                                    onConfirm={() => {
                                                                        if (item.codigo == undefined) return;
                                                                        new AlunoService()
                                                                            .desativar(item.codigo)
                                                                            .then(() => {
                                                                                alunosDisponiveis.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="default" danger size="small" className={Tutoriais.Classes.botaoDesativar}>
                                                                        Desativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            )
                                                        ];
                                                    } else {
                                                        return [
                                                            requerPermissoes.todas(Permissoes.AtivarAluno) && (
                                                                <Popconfirm
                                                                    {...PropriedadesDoPopConfirm("ativar")}
                                                                    onConfirm={() => {
                                                                        if (item.codigo == undefined) return;

                                                                        new AlunoService()
                                                                            .ativar(item.codigo)
                                                                            .then(() => {
                                                                                alunosDisponiveis.refetch();
                                                                            })
                                                                            .catch(() => {});
                                                                    }}
                                                                >
                                                                    <Button type="dashed" size="small">
                                                                        Reativar
                                                                    </Button>
                                                                </Popconfirm>
                                                            ),
                                                        ];
                                                    }
                                                })(),
                                            ]}
                                        />
                                    );
                                },
                            },
                        ]}
                        csv={{
                            enabled: true,
                            filename: "alunos.csv",
                            concat(source) {
                                return [
                                    {
                                        header: "cpf",
                                        data: source.map((v) => v.pessoa?.cpf),
                                    },
                                    {
                                        header: "rne",
                                        data: source.map((v) => v.pessoa?.rne),
                                    },
                                    {
                                        header: "passaporte",
                                        data: source.map((v) => v.pessoa?.passaporte),
                                    },
                                ];
                            },
                            options: {
                                emptyFieldValue: "-",
                            },
                        }}
                    />
                </Col>
            </Row>

            <AdicionarAjuda
                tutoriais={
                    [
                        {
                            titulo: "Como consultar alunos",
                            tutorial: Tutoriais.ConsultarAluno,
                        },

                        requerPermissoes.todas(Permissoes.CriarAluno) && {
                            titulo: "Como cadastrar um novo aluno",
                            tutorial: Tutoriais.AdicionarAluno,
                        },

                        requerPermissoes.todas(Permissoes.AtualizarAluno) && {
                            titulo: "Como editar um aluno",
                            tutorial: Tutoriais.EditarAluno,
                        },

                        requerPermissoes.todas(Permissoes.DesativarAluno) && {
                            titulo: "Como desativar um aluno",
                            tutorial: Tutoriais.DesativarAluno,
                        },
                    ].filter((v) => v != false) as ITutorial[]
                }
            />
        </React.Fragment>
    );
}
